import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Camera } from 'lucide-react'
import { api, type OutreachAnalytics as Analytics, type OutreachCampaign } from '@/api/client'
import { Button, DataTable, StatTile, When, type Column } from '@/ui'
import {
  CampaignHeader,
  ErrorNotice,
  LoadingCard,
  PlainHeader,
  errorMessage,
  isAbortError,
  isValidRouteID,
} from './shared'

export default function OutreachAnalytics() {
  const campaignId = Number(useParams().campaignId)
  const validID = isValidRouteID(campaignId)
  const [campaign, setCampaign] = useState<OutreachCampaign | null>(null)
  const [analytics, setAnalytics] = useState<Analytics | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [snapshotBusy, setSnapshotBusy] = useState(false)
  const [loading, setLoading] = useState(validID)
  const [reloadKey, setReloadKey] = useState(0)
  const routeIDRef = useRef(campaignId)
  routeIDRef.current = campaignId

  useEffect(() => {
    const controller = new AbortController()
    setCampaign(null)
    setAnalytics(null)
    setError(null)
    setSnapshotBusy(false)
    if (!validID) {
      setLoading(false)
      return () => controller.abort()
    }
    setLoading(true)
    void Promise.all([
      api.outreachCampaign(campaignId, controller.signal),
      api.outreachAnalytics(campaignId, controller.signal),
    ])
      .then(([campaignData, analyticsData]) => {
        if (controller.signal.aborted) return
        if (campaignData.id !== campaignId) {
          throw new Error('Outreach response did not match the current campaign route')
        }
        setCampaign(campaignData)
        setAnalytics(analyticsData)
      })
      .catch((err: unknown) => {
        if (!isAbortError(err)) setError(errorMessage(err))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [campaignId, reloadKey, validID])

  const captureSnapshot = async () => {
    if (!campaign || campaign.id !== campaignId) return
    const actionCampaignID = campaignId
    setSnapshotBusy(true)
    setError(null)
    try {
      await api.snapshotOutreachCampaign(actionCampaignID)
      if (routeIDRef.current === actionCampaignID) setReloadKey((value) => value + 1)
    } catch (err) {
      if (routeIDRef.current === actionCampaignID) setError(errorMessage(err))
    } finally {
      if (routeIDRef.current === actionCampaignID) setSnapshotBusy(false)
    }
  }

  const funnel = analytics
    ? ([
        ['발견', analytics.discovered],
        ['적격', analytics.qualified],
        ['연결 요청 보냄', analytics.connections],
        ['연결됨', analytics.connected],
        ['칭찬 보냄', analytics.praise_sent],
        ['답장', analytics.replies],
        ['제안 보냄', analytics.cta_sent],
        ['종료', analytics.closed],
      ] as const)
    : []

  if (!validID) {
    return (
      <div className="space-y-4">
        <PlainHeader title="성과" description="캠페인 주소의 ID가 올바르지 않다." />
        <ErrorNotice message="올바르지 않은 아웃리치 캠페인 ID다." />
      </div>
    )
  }

  const currentCampaign = campaign?.id === campaignId ? campaign : null
  const routeLoading = loading || (!error && !currentCampaign)
  const snapshots = analytics?.snapshots ?? []
  const columns: Column<(typeof snapshots)[number]>[] = [
    {
      key: 'at',
      header: '기록 시각',
      card: 'title',
      cell: (snapshot) => <When iso={snapshot.captured_at} mode="absolute" />,
    },
    {
      key: 'upvotes',
      header: '추천 수',
      align: 'right',
      cell: (snapshot) => <span className="font-medium tabular-nums">{snapshot.upvotes}</span>,
    },
    {
      key: 'change',
      header: '변화',
      align: 'right',
      cell: (snapshot) => {
        const index = snapshots.indexOf(snapshot)
        return (
          <span className="tabular-nums text-muted-foreground">
            {index === 0 ? '—' : signed(snapshot.upvotes - snapshots[index - 1].upvotes)}
          </span>
        )
      },
    },
  ]

  return (
    <div className="space-y-4">
      <CampaignHeader
        campaignId={campaignId}
        title={currentCampaign ? `${currentCampaign.name} 성과` : '아웃리치 성과'}
        description="캠페인 단위 퍼널, 추적 링크 클릭, Product Hunt 추천 수 기록이다. 클릭이 개인에게 닿는 마지막 근거다."
      />
      <ErrorNotice message={error} />

      {routeLoading && <LoadingCard>성과를 불러오는 중…</LoadingCard>}

      {!routeLoading && !error && currentCampaign && analytics && (
        <>
          <section aria-labelledby="funnel-heading" className="space-y-3">
            <h2 id="funnel-heading" className="text-section font-semibold">
              퍼널
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {funnel.map(([label, value]) => (
                <StatTile key={label} label={label} value={value} />
              ))}
            </div>
          </section>

          <section aria-labelledby="rates-heading" className="space-y-3">
            <h2 id="rates-heading" className="text-section font-semibold">
              비율과 클릭
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <StatTile label="연결률" value={formatRate(analytics?.connect_rate)} hint="연결됨 / 보냄" />
              <StatTile label="답장률" value={formatRate(analytics?.reply_rate)} hint="답장 / 칭찬 보냄" />
              <StatTile label="클릭률" value={formatRate(analytics?.click_rate)} hint="제안 뒤 클릭한 사람 / 제안 보냄" />
              <StatTile label="제안 뒤 클릭" value={analytics?.converted_clicks ?? 0} hint="그 사람의 제안 이후 클릭" />
              <StatTile label="전체 클릭" value={analytics?.clicks ?? 0} hint="제안 전 클릭 포함" />
            </div>
          </section>

          <section aria-labelledby="snapshots-heading" className="space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 id="snapshots-heading" className="text-section font-semibold">
                  Product Hunt 추천 수 기록
                </h2>
                <p className="mt-0.5 text-meta text-muted-foreground">
                  캠페인 단위로 관찰한 값이다. 개별 메이커에게 붙이지 않는다.
                </p>
              </div>
              <Button
                icon={Camera}
                loading={snapshotBusy}
                disabled={!campaign?.product_hunt_post_id}
                onClick={() => void captureSnapshot()}
              >
                지금 기록
              </Button>
            </div>
            <DataTable
              label="Product Hunt 추천 수 기록, 시간순"
              columns={columns}
              rows={snapshots}
              rowKey={(snapshot) => snapshot.id}
              empty={<p className="py-6 text-center text-body text-muted-foreground">기록이 없다.</p>}
            />
          </section>
        </>
      )}
    </div>
  )
}

function formatRate(value?: number): string {
  return `${((value ?? 0) * 100).toFixed(1)}%`
}

function signed(value: number): string {
  return value > 0 ? `+${value}` : String(value)
}
